/**
 * useTaskNumberingBaseFieldOptions — lista opcji wyboru „Pole daty numeracji" w oknie typu zadania
 * (odpowiednik `useNumberingBaseFieldOptions` typów dokumentów).
 *
 * Kolejność: opcja domyślna (`''` = brak ustawienia, serwer liczy od daty utworzenia), pola
 * rdzenne dat ze wspólnej stałej `TASK_CORE_DATE_FIELDS` (etykiety i18n; „data utworzenia" jest
 * tylko opcją domyślną, nie osobną pozycją), potem pola DODATKOWE kartoteki `tasks` typu data /
 * data i czas z katalogu pól (`/system-modules/tasks/fields`). Pola dodatkowe o slugu pokrywającym
 * się z rdzennym są pomijane. Do czasu wczytania katalogu (albo przy jego błędzie) lista ma same
 * opcje rdzenne — `isLoading` / `isError` mówią, że jest niepełna.
 *
 * `useTaskNumberingFieldLabel` zwraca nazwę pola daty numeracji po slugu (do komunikatów błędów).
 */

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_TASK_NUMBERING_BASE_SLUG, TASK_CORE_DATE_FIELDS } from '@saas/shared';

import { useSystemModuleFields } from '@/lib/ext-list-columns';
import {
  buildNumberingBaseFieldOptions,
  type NumberingBaseFieldOption,
} from '@/lib/record-types/numbering-base-fields';

/** Opcja listy; `value` = `''` oznacza domyślnie (data utworzenia). */
export type TaskNumberingBaseFieldOption = NumberingBaseFieldOption;

export interface TaskNumberingBaseFieldOptions {
  options: TaskNumberingBaseFieldOption[];
  /** Katalog pól się wczytuje — lista opcji jest niepełna. */
  isLoading: boolean;
  /** Katalog pól się nie wczytał — lista opcji jest niepełna. */
  isError: boolean;
}

/** Slug kartoteki Zadania w katalogu pól. */
const TASKS_MODULE_SLUG = 'tasks';

export function useTaskNumberingBaseFieldOptions(): TaskNumberingBaseFieldOptions {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useSystemModuleFields(TASKS_MODULE_SLUG);

  const options = useMemo(
    () =>
      buildNumberingBaseFieldOptions({
        t,
        coreFields: TASK_CORE_DATE_FIELDS,
        extFields: data,
        leadingOption: { value: '', label: t('tenant.tasks.types.numberingBase.default') },
        omitCoreSlug: DEFAULT_TASK_NUMBERING_BASE_SLUG,
        skipCatalogCoreFields: true,
      }),
    [data, t],
  );

  return { options, isLoading, isError };
}

/**
 * Nazwa pola daty numeracji po slugu: rdzenne — etykieta i18n, dodatkowe — nazwa z katalogu pól
 * zadań, w ostateczności sam slug (np. pole dodatkowe usunięte albo katalog jeszcze niewczytany).
 */
export function useTaskNumberingFieldLabel(): (slug: string) => string {
  const { t } = useTranslation();
  const { data } = useSystemModuleFields(TASKS_MODULE_SLUG);
  return useCallback(
    (slug: string): string => {
      if (slug === DEFAULT_TASK_NUMBERING_BASE_SLUG) {
        return t('tenant.tasks.types.numberingBase.createdAt');
      }
      const core = TASK_CORE_DATE_FIELDS.find((f) => f.slug === slug);
      if (core !== undefined) return t(core.labelKey);
      return data?.find((f) => f.slug === slug)?.name ?? slug;
    },
    [data, t],
  );
}
